import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  BarChart3, 
  Globe, 
  ArrowRight, 
  CheckCircle,
  Building2,
  FileText,
  Users
} from "lucide-react";

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_4aa3e30f-645a-43e8-83ed-b83576a14c9f/artifacts/p43xkrnl_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_4aa3e30f-645a-43e8-83ed-b83576a14c9f/artifacts/h3vxbn0f_SusTek%20Colored%20Logo%20Only.svg";

const LandingPage = () => {
  const frameworks = [
    { name: "UN Global Compact", description: "World's largest corporate sustainability initiative" },
    { name: "GRI Standards", description: "Global Reporting Initiative standards for sustainability reporting" },
    { name: "CDP", description: "Climate disclosure and environmental impact management" },
    { name: "TCFD", description: "Task Force on Climate-related Financial Disclosures" },
    { name: "SBTi", description: "Science Based Targets initiative for emissions reduction" },
    { name: "ISO 14001", description: "Environmental management systems certification" }
  ];

  const benefits = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Risk Mitigation",
      description: "Identify and address ESG risks in your supply chain before they impact your business"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Data-Driven Insights",
      description: "Make informed decisions with comprehensive ESG data from your suppliers"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global Compliance",
      description: "Ensure alignment with international sustainability standards and regulations"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Stakeholder Trust",
      description: "Build confidence with investors, customers, and partners through transparency"
    }
  ];

  const questionnaireAreas = [
    { title: "Governance & Strategy", items: ["ESG Strategy", "Certifications", "Business Ethics", "Data Protection"] },
    { title: "Climate & Emissions", items: ["Scope 1 & 2 Emissions", "Science-Based Targets", "Carbon Reduction Plans"] },
    { title: "Environmental Impact", items: ["Water Management", "Waste & Circularity", "Resource Efficiency"] },
    { title: "Social Responsibility", items: ["Labor Standards", "Health & Safety", "Diversity & Inclusion", "Community Impact"] }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-white">
      {/* Navigation */}
      <nav className="glass-header sticky top-0 z-50" data-testid="landing-nav">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src={LOGO_HORIZONTAL} alt="SusTek" className="h-10" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" className="text-forest hover:bg-forest/10" data-testid="nav-admin-btn">
                Admin Portal
              </Button>
            </Link>
            <Link to="/auth">
              <Button className="btn-primary" data-testid="nav-supplier-login-btn">
                Supplier Login
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="section-padding" data-testid="hero-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <span className="inline-block bg-forest/10 text-forest px-4 py-2 rounded-full text-sm font-medium">
                Sustainable Supply Chain Initiative
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tight">
                Building a More <span className="text-forest">Sustainable</span> Supply Chain Together
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
                Join us in our commitment to environmental, social, and governance excellence. 
                Complete our ESG questionnaire to help us understand and improve our collective impact.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/auth">
                  <Button className="btn-primary flex items-center gap-2" data-testid="hero-get-started-btn">
                    Get Started <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Button variant="outline" className="btn-outline" data-testid="hero-learn-more-btn">
                  Learn More
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in stagger-2">
              <img 
                src="https://images.unsplash.com/photo-1763451327215-4dbaa5315591?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxzdXN0YWluYWJsZSUyMHN1cHBseSUyMGNoYWluJTIwbmF0dXJlJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzE5ODk5NjZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Wind turbines on a rolling green landscape"
                className="rounded-2xl shadow-2xl w-full object-cover max-h-[500px]"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">ISO 14001 Aligned</p>
                    <p className="text-sm text-muted-foreground">Environmental Management</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="why-section">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Why Sustainable Supply Chains Matter
            </h2>
            <p className="text-lg text-muted-foreground">
              In today's interconnected world, supply chain sustainability is not just good practice—it's essential for 
              long-term business success and environmental stewardship.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="card-feature group animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-14 h-14 bg-forest/10 rounded-xl flex items-center justify-center text-forest mb-4 group-hover:bg-forest group-hover:text-white transition-colors">
                  {benefit.icon}
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Framework Alignment Section */}
      <section className="py-16 md:py-24 bg-cream" data-testid="frameworks-section">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Aligned with Global Standards
            </h2>
            <p className="text-lg text-muted-foreground">
              Our ESG questionnaire is designed in alignment with internationally recognized sustainability frameworks 
              and reporting standards.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {frameworks.map((framework, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl border border-border/50 hover:shadow-md transition-all animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`framework-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sage/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-sage" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-1">{framework.name}</h3>
                    <p className="text-sm text-muted-foreground">{framework.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Covered Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="coverage-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-4">
                What the Questionnaire Covers
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Our comprehensive ESG questionnaire captures key sustainability metrics across environmental, 
                social, and governance dimensions. The data helps us:
              </p>
              <ul className="space-y-4">
                {[
                  "Assess supplier ESG performance and maturity",
                  "Identify areas for improvement and collaboration",
                  "Track progress toward sustainability goals",
                  "Ensure compliance with regulatory requirements",
                  "Build a more resilient and responsible supply chain"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {questionnaireAreas.map((area, index) => (
                <div 
                  key={index}
                  className="bg-cream p-6 rounded-xl animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`questionnaire-area-${index}`}
                >
                  <h3 className="font-heading text-lg font-semibold text-forest mb-3">{area.title}</h3>
                  <ul className="space-y-2">
                    {area.items.map((item, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-sage rounded-full"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-forest" data-testid="cta-section">
        <div className="container-main text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Ready to Begin?
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Complete your ESG questionnaire today and join our network of sustainable suppliers. 
              Together, we can make a difference.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/auth">
                <Button className="bg-white text-forest hover:bg-cream px-8 py-3 rounded-full font-medium transition-all" data-testid="cta-start-btn">
                  Start Questionnaire
                </Button>
              </Link>
              <Link to="/admin">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-3 rounded-full font-medium transition-all" data-testid="cta-admin-btn">
                  Admin Portal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-cream border-t border-border" data-testid="footer">
        <div className="container-main">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <img src={LOGO_HORIZONTAL} alt="SusTek" className="h-8" />
              </Link>
              <p className="text-sm text-muted-foreground">
                Building sustainable supply chains for a better tomorrow.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-foreground mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/auth" className="hover:text-forest transition-colors">Supplier Login</Link></li>
                <li><Link to="/admin" className="hover:text-forest transition-colors">Admin Portal</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-foreground mb-4">Frameworks</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>UN Global Compact</li>
                <li>GRI Standards</li>
                <li>CDP / TCFD</li>
                <li>SBTi</li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-foreground mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  <span>Sustainability Team</span>
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span>ESG Inquiries</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} SusTek Sustainable Supply Chain Portal. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
